import React from 'react';
import { useTranslation } from 'react-i18next';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Box, Chip, IconButton, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, Tooltip, Typography,
} from '@mui/material';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';

import EmptyState from '../../../shared/components/EmptyState';
import {
  dataTableSx, FilterCell, HeadLabelCell, SelectColumnFilter, tableActionCellSx,
  TextColumnFilter,
} from '../../../shared/components/TableHeader';
import { activityDisplayName } from '../../../shared/utils/domain';
import { formatEf } from '../../../shared/utils/number';
import { SCOPE_LABEL, SCOPE_SELECT_OPTIONS } from '../../../shared/constants';
import { SCOPE_TOKENS } from '../../../theme/muiTheme';
import { getCategoryDisplay } from '../model/categoryDisplay';

const HEAD_CELLS = [
  { id: 'number', labelKey: 'emissionFactor.column.number', align: 'center', width: 56 },
  { id: 'actions', labelKey: 'common.actions', width: 96 },
  { id: 'scope', labelKey: 'emissionFactor.column.scopeCat', width: 155 },
  { id: 'activity', labelKey: 'emissionFactor.column.activity', width: 250 },
  { id: 'value', labelKey: 'emissionFactor.column.factorValue', align: 'right', width: 135 },
  { id: 'unit', labelKey: 'emissionFactor.column.unit', width: 145 },
  { id: 'source', labelKey: 'emissionFactor.column.sourceFile', width: 205 },
  { id: 'year', labelKey: 'emissionFactor.column.fiscalYear', width: 105 },
  { id: 'origin', labelKey: 'emissionFactor.column.origin', width: 145 },
  { id: 'status', labelKey: 'emissionFactor.column.status', width: 120 },
];

const SCOPE_TONE = {
  SCOPE_1: SCOPE_TOKENS.SCOPE_1,
  SCOPE_2: SCOPE_TOKENS.SCOPE_2,
  SCOPE_3: SCOPE_TOKENS.SCOPE_3,
};

function ScopeChip({ scope }) {
  const theme = useTheme();
  const tone = SCOPE_TONE[scope] || SCOPE_TOKENS.SCOPE_3;
  return (
    <Chip
      size="small"
      label={SCOPE_LABEL[scope] || scope}
      sx={{
        bgcolor: theme.palette.mode === 'dark' ? alpha(tone.color, 0.18) : tone.bg,
        color: theme.palette.mode === 'dark' ? tone.color : tone.dark,
      }}
    />
  );
}

function FilterRow({ filters, options, onFilterChange }) {
  const { t } = useTranslation();
  const originOptions = options.origins.map((option) => ({
    ...option,
    label: t(`emissionFactor.valueOrigins.${option.value}`, {
      defaultValue: option.label,
    }),
  }));
  const filterCells = [
    <FilterCell key="number" />,
    <FilterCell key="actions" />,
    <FilterCell key="scope">
      <SelectColumnFilter value={filters.scope} onChange={(value) => onFilterChange('scope', value)} options={SCOPE_SELECT_OPTIONS} allLabel="All scopes" ariaLabel={t('emissionFactor.filter.scope')} />
    </FilterCell>,
    <FilterCell key="activity"><TextColumnFilter value={filters.activity} onChange={(value) => onFilterChange('activity', value)} ariaLabel={t('emissionFactor.column.activity')} /></FilterCell>,
    <FilterCell key="value"><TextColumnFilter value={filters.factorValue} onChange={(value) => onFilterChange('factorValue', value)} ariaLabel={t('emissionFactor.column.factorValue')} /></FilterCell>,
    <FilterCell key="unit"><SelectColumnFilter value={filters.unit} onChange={(value) => onFilterChange('unit', value)} options={options.units} allLabel="All units" ariaLabel={t('emissionFactor.column.unit')} /></FilterCell>,
    <FilterCell key="source"><SelectColumnFilter value={filters.sourceFile} onChange={(value) => onFilterChange('sourceFile', value)} options={options.sourceFiles} allLabel="All source files" ariaLabel={t('emissionFactor.column.sourceFile')} /></FilterCell>,
    <FilterCell key="year" />,
    <FilterCell key="origin"><SelectColumnFilter value={filters.origin} onChange={(value) => onFilterChange('origin', value)} options={originOptions} allLabel="All" ariaLabel={t('emissionFactor.column.origin')} /></FilterCell>,
    <FilterCell key="status">
      <SelectColumnFilter
        value={filters.status}
        onChange={(value) => onFilterChange('status', value)}
        options={[
          { value: 'active', label: t('emissionFactor.status.active') },
          { value: 'inactive', label: t('emissionFactor.status.inactive') },
        ]}
        allLabel={t('emissionFactor.status.all')}
        ariaLabel={t('emissionFactor.status.filter')}
      />
    </FilterCell>,
  ];
  return (
    <TableRow>
      {filterCells}
    </TableRow>
  );
}

function FactorRow({
  factor, rowNumber, onEdit, onHistory, onSourceClick, downloadingSourceId, onStatusChange,
}) {
  const { t } = useTranslation();
  const category = getCategoryDisplay(t, factor.category);
  const isDerivedGreenFactor = factor.item_key === 'SCOPE_2.GREEN'
    || factor.item_key === 'SCOPE_2.POLICY::LRC_GREEN_ZERO::v1';
  const valueOrigin = factor.value_origin || 'UNKNOWN';

  return (
    <TableRow hover>
      <TableCell align="center">
        <Typography variant="caption" color="text.secondary">
          {rowNumber}
        </Typography>
      </TableCell>
      <TableCell sx={{ ...tableActionCellSx, minWidth: 96, px: 1 }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'nowrap' }}>
          <Tooltip title={isDerivedGreenFactor
            ? t('emissionFactor.form.greenReadonly')
            : t('common.edit')}
          >
            <span>
              <IconButton
                size="small"
                disabled={isDerivedGreenFactor}
                onClick={() => onEdit(factor)}
                aria-label={t('common.edit')}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('common.history')}>
            <IconButton size="small" onClick={() => onHistory(factor)} aria-label={t('common.history')}>
              <HistoryRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
      <TableCell>
        <ScopeChip scope={factor.scope} />
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
          {category.short}
        </Typography>
      </TableCell>
      <TableCell sx={{ maxWidth: 290 }}>
        <Typography variant="body2" fontWeight={700} noWrap title={activityDisplayName(factor)}>
          {activityDisplayName(factor)}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap component="div">
          {category.label} · {factor.registration_no || factor.ef_external_id}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 750 }}>
          {formatEf(factor.ef_value)}
        </Typography>
      </TableCell>
      <TableCell><Typography variant="caption">{factor.ef_unit}</Typography></TableCell>
      <TableCell>
        {factor.source_file ? (
          <Tooltip title={t('emissionFactor.downloadHint')}>
            <Chip
              clickable
              disabled={Boolean(downloadingSourceId)}
              size="small"
              icon={<AttachFileRoundedIcon />}
              label={factor.source_file.name}
              onClick={() => onSourceClick(factor)}
              aria-busy={downloadingSourceId === factor.source_file.id}
              color="info"
              variant="outlined"
              sx={{ maxWidth: 190, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
            />
          </Tooltip>
        ) : <Typography variant="caption" color="text.secondary">—</Typography>}
      </TableCell>
      <TableCell><Typography variant="caption" fontWeight={700}>{factor.fiscal_year || '—'}</Typography></TableCell>
      <TableCell>
        <Chip
          size="small"
          label={t(`emissionFactor.valueOrigins.${valueOrigin}`, {
            defaultValue: valueOrigin.replaceAll('_', ' '),
          })}
          color={valueOrigin === 'MANUAL' ? 'warning' : 'default'}
          variant="outlined"
        />
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          label={factor.is_active ? t('emissionFactor.status.active') : t('emissionFactor.status.inactive')}
          color={factor.is_active ? 'success' : 'default'}
          variant={factor.is_active ? 'filled' : 'outlined'}
          onClick={() => onStatusChange(
            factor,
            factor.is_active ? 'INACTIVE' : 'ACTIVE',
            t('emissionFactor.status.defaultReason'),
          )}
        />
      </TableCell>
    </TableRow>
  );
}

export default function EmissionFactorTable({
  factors,
  filters,
  options,
  onFilterChange,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onEdit,
  onHistory,
  onSourceClick,
  downloadingSourceId,
  onStatusChange,
}) {
  const { t } = useTranslation();
  const pageRows = factors.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={(theme) => ({ ...dataTableSx(theme), minWidth: 1336 })}>
            <TableHead sx={{ '& .MuiTableCell-root': { textTransform: 'none !important' } }}>
            <TableRow>
              {HEAD_CELLS.map((cell) => (
                <HeadLabelCell key={cell.id} align={cell.align} width={cell.width}>
                  {t(cell.labelKey)}
                </HeadLabelCell>
              ))}
            </TableRow>
            <FilterRow filters={filters} options={options} onFilterChange={onFilterChange} />
          </TableHead>
          <TableBody>
            {pageRows.map((factor, index) => (
              <FactorRow
                key={factor.id}
                factor={factor}
                rowNumber={(page * rowsPerPage) + index + 1}
                onEdit={onEdit}
                onHistory={onHistory}
                onSourceClick={onSourceClick}
                downloadingSourceId={downloadingSourceId}
                onStatusChange={onStatusChange}
              />
            ))}
            {pageRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={HEAD_CELLS.length} sx={{ border: 0 }}>
                  <EmptyState title={t('common.noResults')} description={t('common.noResultsHint')} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={factors.length}
        page={page}
        onPageChange={(event, next) => onPageChange(next)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => onRowsPerPageChange(Number(event.target.value))}
        rowsPerPageOptions={[10, 25, 50]}
        labelRowsPerPage={t('common.rowsPerPage')}
      />
    </Box>
  );
}
